import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { lazy, type ReactNode } from 'react'
import AuthGuard from './AuthGuard'
import EmbeddedShell from './EmbeddedShell'
import RouteError from './RouteError'
import { BridgeProvider } from './BridgeProvider'
import SvShell from '@/roles/sv/SvShell'
import { SV_HOME, svFullscreenRoutes, svRoutes } from '@/roles/sv/routes'
import { titleForPath as svTitleForPath } from '@/roles/sv/nav'
import OwnerShell from '@/roles/owner/OwnerShell'
import { ownerRoutes } from '@/roles/owner/routes'
import { OWNER_PATH, titleForPath as ownerTitleForPath } from '@/roles/owner/nav'
import { hasHostShell, isHostedWebShell } from '@/shared/bridge/env'
import { normalizeRole, type Role } from '@/shared/role'
import { useAuthStore } from '@/shared/store/authStore'
import { page } from '@/shared/ui'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

const HOME_FOR: Record<Role, string> = { sv: SV_HOME, owner: OWNER_PATH.home }

function useRole() {
  return normalizeRole(useAuthStore((state) => state.user?.role))
}

/** 다른 역할의 화면으로 들어오면 내 역할의 홈으로 돌려보낸다. */
function RoleGate({ role, children }: { role: Role; children: ReactNode }) {
  const current = useRole()
  if (current !== role) return <Navigate to={HOME_FOR[current]} replace />
  return <>{children}</>
}

function RoleHome() {
  return <Navigate to={HOME_FOR[useRole()]} replace />
}

export const router = createBrowserRouter([
  { path: '/login', element: page(LoginPage), errorElement: <RouteError /> },
  {
    path: '/',
    element: (
      <AuthGuard>
        <Outlet />
      </AuthGuard>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <RoleHome /> },
      {
        element: (
          <RoleGate role="sv">
            <BridgeProvider homePath={SV_HOME} titleForPath={svTitleForPath}>
              <Outlet />
            </BridgeProvider>
          </RoleGate>
        ),
        children: [
          {
            element: hasHostShell() && !isHostedWebShell() ? <EmbeddedShell /> : <SvShell />,
            children: svRoutes,
          },
          ...svFullscreenRoutes,
        ],
      },
      {
        path: 'owner',
        element: (
          <RoleGate role="owner">
            <BridgeProvider homePath={OWNER_PATH.home} titleForPath={ownerTitleForPath}>
              {hasHostShell() && !isHostedWebShell() ? <EmbeddedShell /> : <OwnerShell />}
            </BridgeProvider>
          </RoleGate>
        ),
        children: [
          { index: true, element: <Navigate to={OWNER_PATH.home} replace /> },
          ...ownerRoutes,
        ],
      },
    ],
  },
  { path: '*', element: page(NotFoundPage) },
])
